<script setup>
/**
 * Список команд для жюри: загрузка профиля → списка команд → результатов оценок.
 * Запросы идут строго по порядку; ошибка любого шага показывается в интерфейсе (без alert).
 */
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { ref, computed, onMounted } from "vue";
import axios from "axios";

const store = useStore();
const router = useRouter();

const isLoading = ref(false);
/** Сообщение об ошибке загрузки: 'NETWORK' | 'DATA' | null */
const loadError = ref(null);

/** Профиль с сервера (role, id, …) — обновляется при каждом заходе на экран */
const userData = ref(null);
/** Команды для отображения (массив элементов с original) */
const userTeams = ref([]);
/** Результаты оценок для расчёта рейтинга и статуса карточки */
const results = ref([]);

const token = computed(() => store.getters["authModule/token"]);
const userId = computed(() => store.getters["authModule/userId"]);

onMounted(() => {
	void init();
});

/**
 * Собирает URL API (как раньше), с экранированием токена в query.
 */
function buildUrls() {
	const admin = process.env.VUE_APP_ADMIN_URL + "/api";
	const publicBase = process.env.VUE_APP_URL + "/api";
	const t = encodeURIComponent(token.value || "");
	const id = encodeURIComponent(String(userId.value ?? ""));
	return {
		userData: `${admin}/userData.php?token=${t}&id=${id}`,
		teams: (role) =>
			`${publicBase}/teams/${t}/${encodeURIComponent(String(role))}`,
		results: (role) =>
			`${admin}/results.php?token=${t}&id=${id}&role=${encodeURIComponent(String(role))}`,
	};
}

/**
 * Из ответа /teams достаёт массив команд; при неожиданной форме — пустой массив (без падения UI).
 */
function extractTeamsList(teamsPayload) {
	if (teamsPayload && Array.isArray(teamsPayload.data)) {
		return teamsPayload.data;
	}
	return [];
}

async function init() {
	loadError.value = null;
	isLoading.value = true;

	store.commit("menuModule/initMenu");

	// Нет сессии — на экран входа (маршрут с именем login)
	if (!token.value || userId.value == null) {
		isLoading.value = false;
		await router.push({ name: "login" });
		return;
	}

	const urls = buildUrls();

	try {
		// 1) Актуальные данные пользователя (в т.ч. role для остальных запросов)
		const { data: profile } = await axios.get(urls.userData);
		if (!profile || typeof profile !== "object" || profile.role == null) {
			loadError.value = "DATA";
			userTeams.value = [];
			return;
		}
		userData.value = profile;
		store.commit("userModule/setUserData", profile);

		// 2) Список команд под роль
		const teamsRes = await axios.get(urls.teams(profile.role));
		const list = extractTeamsList(teamsRes.data);
		userTeams.value = list;
		store.commit("teamsModule/setTeams", list);

		// 3) Результаты для рейтинга и подсветки карточек
		const { data: resultsPayload } = await axios.get(
			urls.results(profile.role),
		);
		results.value = Array.isArray(resultsPayload) ? resultsPayload : [];
	} catch (e) {
		console.error("TheTeamsForJuri init:", e);
		loadError.value = "NETWORK";
		userTeams.value = [];
		results.value = [];
	} finally {
		isLoading.value = false;
	}
}

/**
 * Сумма баллов текущего жюри по команде + метрика из карточки.
 */
const createRating = (teamId, metrika) => {
	if (!Array.isArray(results.value) || !userData.value) {
		return Number(metrika) || 0;
	}
	const myId = userData.value.id;
	let criteriaSum = 0;
	for (const item of results.value) {
		if (+item.teamId === +teamId && +item.userId === +myId) {
			criteriaSum += +item.result;
		}
	}
	return criteriaSum + (+metrika || 0);
};

/**
 * Для role 100 карточки всегда «завершённые»; иначе — есть ли хотя бы одна оценка от этого userId по команде.
 * @param teamId — id команды
 * @param currentUserId — id жюри из стора (явное имя, чтобы не путать с геттером userId)
 */
const hasAssessmentForTeam = (teamId, currentUserId) => {
	if (!userData.value) {
		return false;
	}
	if (userData.value.role === 100) {
		return true;
	}
	if (!Array.isArray(results.value)) {
		return false;
	}
	for (const item of results.value) {
		if (+item.teamId === +teamId && +item.userId === +currentUserId) {
			return true;
		}
	}
	return false;
};

/** Стабильный ключ для v-for (fallback на index при неполных данных) */
const teamRowKey = (team, index) => {
	const o = team?.original;
	if (o != null && o.team_id != null && o.TaskId != null) {
		return `${o.team_id}-${o.TaskId}`;
	}
	return `team-row-${index}`;
};
</script>

<template>
	<section class="content-header">
		<div class="container-fluid">
			<div class="row">
				<div class="col">
					<h3>
						Команды
						<span v-if="userTeams.length > 0"
							>({{ userTeams.length }})</span
						>
					</h3>
				</div>
			</div>
		</div>
	</section>

	<!-- /team/:teamId/:taskId — критерии привязаны к задаче -->
	<section class="content mt-2">
		<div class="container-fluid">
			<div
				v-if="loadError === 'NETWORK'"
				class="alert alert-danger"
				role="alert"
			>
				Не удалось загрузить данные. Проверьте соединение или войдите
				снова.
			</div>
			<div
				v-else-if="loadError === 'DATA'"
				class="alert alert-warning"
				role="alert"
			>
				Ответ сервера не содержит профиля пользователя. Обновите страницу
				или войдите снова.
			</div>

			<div v-if="!isLoading && !loadError && userTeams.length === 0" class="text-muted my-3">
				Команд для оценки пока нет.
			</div>

			<div v-if="userTeams.length > 0" class="row">
				<div
					v-for="(team, index) in userTeams"
					:key="teamRowKey(team, index)"
					class="col-md-6 col-sm-12 mb-2"
				>
					<router-link
						:to="
							'/team/' +
							team['original']['team_id'] +
							'/' +
							team['original']['TaskId']
						"
						class="card-link"
					>
						<div class="card">
							<div
								:class="[
									'card-header',
									hasAssessmentForTeam(
										team['original']['team_id'],
										userId,
									)
										? 'bg-completed'
										: 'bg-no-completed',
								]"
							>
								<strong>{{ team["original"]["team"] }}</strong>
							</div>
							<div class="card-body" style="min-height: 180px">
								<div class="team-rating">
									{{
										createRating(
											team["original"]["team_id"],
											team["original"]["metrika_advance"],
										)
									}}
								</div>

								<strong>Задача:</strong><br />
								{{ team["original"]["Task"] }}
							</div>
						</div>
					</router-link>
				</div>
			</div>

			<div class="d-flex justify-content-center">
				<div
					v-if="isLoading"
					class="spinner-border text-primary m-4"
					role="status"
				></div>
			</div>
		</div>
	</section>
</template>

<style scoped>
.bg-completed {
	background-color: #80b5d3;
	color: #fff;
}
.bg-no-completed {
	background-color: #ff5858;
	color: #fff;
}
.team-rating {
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	width: 75px;
	height: 75px;
	border: 3px solid #ff5858;
	border-radius: 50%;
	margin: 0 auto 10px auto;
	color: #ff5858;
	font-size: 24px;
	font-weight: bold;
}
</style>
